/**
 * Panel para renumerar correlativos duplicados de tesorería (v3.28.25).
 * Consume /api/maintenance/treasury-renumber/preview y /apply.
 *
 * A diferencia del panel viejo (`DuplicatesRepairPanel`) que BORRABA los duplicados,
 * este panel RENUMERA la 2ª secuencia al final y actualiza todas las referencias.
 * No se pierde ninguna operación real.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { RefreshCw, ShieldCheck, Wand2, AlertTriangle, History, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLL_LABELS = {
  treasury_payment_orders: 'Órdenes de Pago',
  treasury_collections: 'Cobranzas',
  treasury_transfers: 'Transferencias',
  treasury_adjustments: 'Ajustes',
};

function downloadCsv(run) {
  const rows = [[
    'Colección', 'Nº Original', 'Nº Nuevo',
    'Fecha op (renumerado)', 'Importe (renumerado)', 'Contraparte (renumerado)', 'Concepto (renumerado)', 'Descripción (renumerado)',
    'ID par 1ª sec', 'Fecha op (1ª sec)', 'Importe (1ª sec)', 'Contraparte (1ª sec)',
    'ID renumerado', 'Fecha creación',
  ]];
  (run.mapping || []).forEach((x) => rows.push([
    COLL_LABELS[x.coll] || x.coll,
    x.old, x.new,
    (x.date || '').slice(0, 10),
    x.amount != null ? Number(x.amount).toFixed(2) : '',
    x.party || '',
    x.concept || '',
    x.description || '',
    x.pair_id || '',
    (x.pair_date || '').slice(0, 10),
    x.pair_amount != null ? Number(x.pair_amount).toFixed(2) : '',
    x.pair_party || '',
    x.id || '',
    (x.created_at || '').slice(0, 19).replace('T', ' '),
  ]));
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const ts = new Date(run.timestamp).toISOString().slice(0, 16).replace(/[-:T]/g, '');
  a.href = url;
  a.download = `treasury_renumber_${ts}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const fmtMoney = (v) => v == null ? '' : Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2 });
const fmtDate = (v) => v ? String(v).slice(0, 10) : '';

export function TreasuryRenumberPanel() {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/treasury-renumber/preview`, { withCredentials: true });
      setPlan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el plan');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/treasury-renumber/history`, { withCredentials: true });
      setHistory(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el historial');
    }
  };

  useEffect(() => { load(); loadHistory(); }, []);

  const doApply = async () => {
    setApplying(true);
    setConfirm(false);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/maintenance/treasury-renumber/apply`, {}, { withCredentials: true });
      setLastResult(r.data);
      if (r.data.applied) toast.success(`Renumerados ${r.data.renumbered} correlativos`);
      else toast.info('Sin duplicados para renumerar');
      await load();
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al aplicar');
    } finally {
      setApplying(false);
    }
  };

  const total = plan?.total_renumbered || 0;

  return (
    <Card className={total > 0 ? 'border-amber-300' : ''} data-testid="treasury-renumber-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-emerald-600" /> Renumerar correlativos duplicados de Tesorería
          {total > 0 && <Badge className="bg-amber-500">{total} pendiente(s)</Badge>}
          {total === 0 && !loading && <Badge variant="outline" className="text-emerald-700 border-emerald-300">Todo OK</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Detecta números correlativos duplicados en <b>Órdenes de Pago</b>, <b>Cobranzas</b>, <b>Transferencias</b> y <b>Ajustes</b>. Si encuentra pares, renumera la <b>segunda secuencia (la más nueva)</b> al final y actualiza <b>todas las referencias</b> en asientos contables, movimientos de tesorería y cuentas corrientes. <b>No se pierde información</b>.
        </p>

        {loading ? (
          <p className="text-muted-foreground">Analizando…</p>
        ) : total === 0 ? (
          <div className="rounded bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-emerald-800" data-testid="renumber-clean">
            <ShieldCheck className="w-4 h-4" /> Sin duplicados. Contadores alineados al máximo real.
          </div>
        ) : (
          <>
            <Alert variant="warning" className="border-amber-300 bg-amber-50">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <AlertTitle className="text-amber-800">Se detectaron {total} correlativo(s) duplicado(s)</AlertTitle>
              <AlertDescription className="text-amber-800 text-xs">
                Se renumerarán los más nuevos por fecha de creación. La 1ª secuencia se preserva intacta.
              </AlertDescription>
            </Alert>
            <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
              {Object.entries(plan.summary_by_collection || {}).map(([coll, n]) => (
                <div key={coll}><b>{COLL_LABELS[coll] || coll}</b>: {n} a renumerar</div>
              ))}
              <div className="pt-2 border-t border-muted mt-2">
                <b>Contadores resultantes:</b>{' '}
                {Object.entries(plan.final_counters || {}).map(([coll, v]) => (
                  <span key={coll} className="font-mono mr-3">{COLL_LABELS[coll]}: {v}</span>
                ))}
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr><th className="text-left p-1.5">Colección</th><th className="text-left p-1.5">Actual</th><th className="text-left p-1.5">→ Nuevo</th></tr>
                </thead>
                <tbody>
                  {(plan.detail || []).slice(0, 200).map((x, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1.5">{COLL_LABELS[x.coll] || x.coll}</td>
                      <td className="p-1.5 font-mono">{x.old}</td>
                      <td className="p-1.5 font-mono text-emerald-700">{x.new}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(plan.detail?.length || 0) > 200 && <p className="p-2 text-muted-foreground">… +{plan.detail.length - 200} más</p>}
            </div>
          </>
        )}

        {lastResult && lastResult.applied && (
          <div className="rounded bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-1" data-testid="renumber-result">
            <p className="font-semibold text-emerald-800">✓ Renumeración aplicada</p>
            <p>Docs renumerados: <b>{lastResult.renumbered}</b></p>
            <p>Movimientos actualizados: <b>{lastResult.stats?.movements_updated}</b> · Asientos: <b>{lastResult.stats?.journal_updated}</b> · CC proveedor: <b>{lastResult.stats?.supplier_cc_updated}</b> · CC residente: <b>{lastResult.stats?.resident_cc_updated}</b> · Anticipos: <b>{lastResult.stats?.advances_updated}</b></p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="renumber-recheck-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Volver a verificar
          </Button>
          <Button onClick={() => setConfirm(true)} disabled={total === 0 || applying} data-testid="renumber-apply-btn">
            <Wand2 className="w-3.5 h-3.5 mr-1" /> {applying ? 'Aplicando…' : 'Renumerar'}
          </Button>
        </div>

        {/* Historial de renumeraciones aplicadas */}
        {history?.runs?.length > 0 && (
          <details className="rounded-md border border-slate-200 bg-slate-50/50" open={historyOpen}
            onToggle={(e) => setHistoryOpen(e.target.open)} data-testid="renumber-history">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              Historial de renumeraciones aplicadas
              <Badge variant="outline" className="ml-auto text-xs">{history.runs.length} corrida(s)</Badge>
            </summary>
            <div className="p-3 space-y-4">
              {history.runs.map((run, i) => (
                <div key={i} className="rounded border bg-white p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs">
                      <div className="font-semibold text-slate-800">
                        {new Date(run.timestamp).toLocaleString('es-AR')} · {run.user_email}
                      </div>
                      <div className="text-muted-foreground">
                        Renumerados: <b>{run.renumbered}</b> · Movs actualizados: <b>{run.stats?.movements_updated || 0}</b> · Asientos: <b>{run.stats?.journal_updated || 0}</b> · CC prov/res: <b>{(run.stats?.supplier_cc_updated || 0) + (run.stats?.resident_cc_updated || 0)}</b>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadCsv(run)} data-testid={`renumber-csv-btn-${i}`}>
                      <Download className="w-3.5 h-3.5 mr-1" /> CSV
                    </Button>
                  </div>
                  {run.mapping?.length > 0 && (
                    <div className="max-h-96 overflow-auto rounded border">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0 z-10">
                          <tr>
                            <th className="text-left p-1.5 whitespace-nowrap">Colección</th>
                            <th className="text-left p-1.5 whitespace-nowrap">Nº Original</th>
                            <th className="text-left p-1.5 whitespace-nowrap">→ Nº Nuevo</th>
                            <th className="text-left p-1.5 whitespace-nowrap">Fecha op</th>
                            <th className="text-right p-1.5 whitespace-nowrap">Importe</th>
                            <th className="text-left p-1.5 whitespace-nowrap">Contraparte / Concepto</th>
                            <th className="text-left p-1.5 whitespace-nowrap bg-blue-50">Par (1ª sec)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {run.mapping.map((x, j) => (
                            <tr key={j} className="border-t hover:bg-slate-50">
                              <td className="p-1.5">{COLL_LABELS[x.coll] || x.coll}</td>
                              <td className="p-1.5 font-mono">{x.old}</td>
                              <td className="p-1.5 font-mono text-emerald-700 font-semibold">{x.new}</td>
                              <td className="p-1.5 whitespace-nowrap">{fmtDate(x.date)}</td>
                              <td className="p-1.5 text-right whitespace-nowrap">${fmtMoney(x.amount)}</td>
                              <td className="p-1.5">
                                <div className="font-medium">{x.party || '—'}</div>
                                {x.concept && <div className="text-muted-foreground text-[10px]">{x.concept}</div>}
                                {x.description && <div className="text-muted-foreground text-[10px] italic">"{x.description.slice(0,60)}"</div>}
                              </td>
                              <td className="p-1.5 bg-blue-50/40 text-[10px]">
                                <div>{fmtDate(x.pair_date)} · <span className="font-semibold">${fmtMoney(x.pair_amount)}</span></div>
                                <div className="text-muted-foreground">{x.pair_party || '—'}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="renumber-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar renumeración</DialogTitle>
            <DialogDescription>
              Se renumerarán <b>{total}</b> correlativo(s) y se actualizarán todas las referencias en asientos, movimientos y CCs. La operación es <b>segura</b>: no elimina ni modifica importes, sólo cambia los números correlativos. Se registrará en la auditoría.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>Cancelar</Button>
            <Button onClick={doApply} disabled={applying} data-testid="renumber-confirm-btn">
              {applying ? 'Aplicando…' : 'Confirmar y renumerar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default TreasuryRenumberPanel;
